import '@mobiscroll/react/dist/css/mobiscroll.min.css'
import { setOptions } from '@mobiscroll/react'
import { useMemo, useRef, useState, useEffect } from 'react'
import styled from 'styled-components'
import '../common/rotaStyles.css'
import { PageHeader, Tabs, EmptyState, Edit } from '@lmc/ui-kit'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Home } from '@lmc/ts-common'
import { getNominative } from '@lmc/common/utils'
import { CalendarOptions, themeOptions } from '../common/constants'
import UserRota from './UserRota/UserRota'
import ResidentRota from './ResidentRota/ResidentRota'
import {
  RotaBuilderFiltersType,
  RotaBuilderQueryFiltersType,
} from 'screens/Rostering/types'
import { DAILY, WEEKLY } from 'screens/Rostering/constants'
import useCountChanges from 'screens/Rostering/hooks/useCountChanges'
import useRosterPublished from 'screens/Rostering/hooks/useRosterPublished'
import ShiftRota from './ShiftRota/ShiftRota'
import checkHasFilters from 'utils/checkHasFIlters'
import Rota from '@lmc/ui-kit/src/dataDisplay/EmptyState/icons/Rota'
import { Spinner } from 'components/generic'
import { Button, Text } from '@lmc/ui-kit/src/general'
import { useNavigate } from 'react-router-dom'
import useFilterParamsGeneric from 'hooks/useFilterParamsGeneric'
import dayjs from 'dayjs'
import useMixpanelEvent, { MixpanelEvents } from 'hooks/useMixpanelEvent'

setOptions(themeOptions)

const PublishedRota = () => {
  const { t } = useTranslation()

  const sendEvent = useMixpanelEvent()

  const [timelineView, setTimelineView] = useState(CalendarOptions.weekly)

  const { filterParamsGeneric, setFilterParams } =
    useFilterParamsGeneric<RotaBuilderQueryFiltersType>({
      selectedDate: dayjs().format('YYYY-MM-DD'),
    })

  const currentFiltersRef = useRef<RotaBuilderFiltersType>({})

  const filterParams: RotaBuilderFiltersType & { selectedDate?: undefined } =
    useMemo(() => {
      const newFilters: RotaBuilderFiltersType & { selectedDate?: undefined } =
        {
          ...filterParamsGeneric,
          selectedDate: undefined,
        }

      delete newFilters.selectedDate

      currentFiltersRef.current = newFilters

      return newFilters
    }, [filterParamsGeneric])

  const selectedDate = useMemo(
    () => new Date(filterParamsGeneric.selectedDate),
    [filterParamsGeneric.selectedDate],
  )

  const setSelectedDate = (newDate: Date) => {
    const currentFilters = currentFiltersRef.current
    setFilterParams(() => ({
      ...currentFilters,
      selectedDate: dayjs(newDate).format('YYYY-MM-DD'),
    }))
  }

  const [active, setActive] = useState('1')

  const navigate = useNavigate()

  const onFilterChange = (value: RotaBuilderFiltersType) => {
    setFilterParams((prevFilters) => ({
      ...prevFilters,
      ...value,
    }))
  }

  const handleTabs = (tab: string) => {
    setActive(tab)

    const getTabLabel = (tabKey: string) => {
      switch (tabKey) {
        case '1':
          return t('BY_TEAM_MEMBER')
        case '2':
          return residentsLabel
        case '3':
          return 'By Shift'
        default:
          return 'Unknown'
      }
    }

    sendEvent(MixpanelEvents.ROTA_BUILDER_TAB_VIEWED, {
      tabKey: tab,
      tabLabel: getTabLabel(tab),
      isInitialLoad: false,
      viewType: isDailyView ? 'daily' : 'weekly',
      timelineView: timelineView,
      source: 'published_rota',
    })
  }

  const isDailyView =
    timelineView === CalendarOptions.daily ||
    timelineView === CalendarOptions.dailyZoomed

  const isByShiftTab = active === '3'

  const { countChangesData, refetchCountChanges } = useCountChanges({
    selectedDate,
    intervalType: isDailyView ? DAILY : WEEKLY,
    filterParams: {
      ...filterParams,
      ...(isByShiftTab && { siteShift: true }),
    },
  })

  const hasFilters = checkHasFilters(filterParams)

  const {
    hasEvents: hasUnfilteredEvents,
    isPublishedRotaLoading: isLoadingUnfilteredEvents,
  } = useRosterPublished({
    selectedDate,
    intervalType:
      timelineView === CalendarOptions.daily ||
      timelineView === CalendarOptions.dailyZoomed
        ? DAILY
        : WEEKLY,
    filterParams: {
      ...(isByShiftTab && { siteShift: true }),
    },
    totalEvents: 0,
    recursionEnabled: true,
  })

  const {
    shifts,
    users,
    residents,
    usersEvents,
    residentsEvents,
    isPublishedRotaLoading,
    invalid,
    metadata,
  } = useRosterPublished({
    selectedDate,
    intervalType:
      timelineView === CalendarOptions.daily ||
      timelineView === CalendarOptions.dailyZoomed
        ? DAILY
        : WEEKLY,
    filterParams: {
      ...filterParams,
      ...(isByShiftTab && { siteShift: true }),
    },
    totalEvents: hasFilters
      ? countChangesData.filteredTotal || 0
      : countChangesData.totalEvents || 0,
    recursionEnabled: true,
  })

  const preferredPeopleNominative = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative,
  )
  const residentsLabel = `By ${getNominative(
    preferredPeopleNominative,
    'Resident',
  )}`

  useEffect(() => {
    sendEvent(MixpanelEvents.ROTA_BUILDER_TAB_VIEWED, {
      tabKey: '1',
      tabLabel: t('BY_TEAM_MEMBER'),
      isInitialLoad: true,
      viewType: isDailyView ? 'daily' : 'weekly',
      timelineView: timelineView,
      source: 'published_rota',
    })
  }, [])

  return (
    <Container>
      <PageHeader
        title={t('PUBLISHED_ROTA')}
        subtitle={t('PUBLISHED_ROTA_SUBTITLE')}
      />
      {isLoadingUnfilteredEvents && <Spinner />}
      {hasUnfilteredEvents && !isLoadingUnfilteredEvents && (
        <Tabs
          tabBarStyle={tabBarStyle}
          activeKey={active}
          onChange={handleTabs}
          items={[
            {
              key: '1',
              label: t('BY_TEAM_MEMBER'),
              children: (
                <UserRota
                  selectedDate={selectedDate}
                  setSelectedDate={setSelectedDate}
                  timelineView={timelineView}
                  setTimelineView={setTimelineView}
                  publishedRota
                  invalid={invalid}
                  resources={users}
                  events={usersEvents}
                  isLoading={isPublishedRotaLoading}
                  filterParams={filterParams}
                  onFilterChange={onFilterChange}
                  isDailyView={isDailyView}
                  refetchCountChanges={refetchCountChanges}
                  countChangesData={countChangesData}
                  metadata={metadata}
                  hasFilters={hasFilters}
                />
              ),
            },
            {
              key: '2',
              label: residentsLabel,
              children: (
                <ResidentRota
                  selectedDate={selectedDate}
                  setSelectedDate={setSelectedDate}
                  timelineView={timelineView}
                  setTimelineView={setTimelineView}
                  publishedRota
                  resources={residents}
                  events={residentsEvents}
                  isLoading={isPublishedRotaLoading}
                  filterParams={filterParams}
                  onFilterChange={onFilterChange}
                  isDailyView={isDailyView}
                  refetchCountChanges={refetchCountChanges}
                  countChangesData={countChangesData}
                  metadata={metadata}
                  hasFilters={hasFilters}
                />
              ),
            },
            {
              key: '3',
              label: 'By Shift',
              children: (
                <ShiftRota
                  selectedDate={selectedDate}
                  setSelectedDate={setSelectedDate}
                  timelineView={timelineView}
                  setTimelineView={setTimelineView}
                  publishedRota
                  resources={shifts}
                  events={residentsEvents?.map((item) => ({
                    ...item,
                    resource: item.instanceType || '',
                    title: item.instanceType || '',
                  }))}
                  isLoading={isPublishedRotaLoading}
                  filterParams={filterParams}
                  onFilterChange={onFilterChange}
                  isDailyView={isDailyView}
                  refetchCountChanges={refetchCountChanges}
                  countChangesData={countChangesData}
                  hasFilters={hasFilters}
                />
              ),
            },
          ]}
        />
      )}
      {!hasUnfilteredEvents && !isLoadingUnfilteredEvents && (
        <EmptyState
          title={t('NOTHING_PUBLISHED_YET')}
          description={
            <>
              <EmptyStateDescription level={1}>
                {t('NOTHING_PUBLISHED_YET_DESCRIPTION_1')}
                <br />
                {t('NOTHING_PUBLISHED_YET_DESCRIPTION_2')}
              </EmptyStateDescription>
              <Button onClick={() => navigate('../builder')}>
                <Edit />
                {t('OPEN_BUILDER')}
              </Button>
            </>
          }
          icon={<Rota />}
        />
      )}
    </Container>
  )
}

const tabBarStyle = { paddingLeft: 24 }

const EmptyStateDescription = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
  text-align: center;
`

const Container = styled.div`
  padding: 24px 48px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

export default PublishedRota
